// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Switch, Tooltip } from '@mui/material';
import { ChartContainer } from '@shared/ui-kit/components';
import { classNames } from '@shared/ui-kit/utils';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { useTranslation } from 'react-i18next';
import { type DualSeriesChartData } from './types';
import { useDualSeriesAreaChart } from './use-dual-series-area-chart';

export const DualSeriesAreaChart = defineComponent({
  displayName: 'DualSeriesAreaChart',
  useSetup: ({
    title,
    currency,
    splitToggleLabel,
    splitToggleTooltip,
    data,
  }: {
    title: string;
    currency: string;
    splitToggleLabel: string;
    splitToggleTooltip: string;
    data: DualSeriesChartData;
  }) => {
    const { t } = useTranslation();
    const chartSetup = useDualSeriesAreaChart(data, currency);

    return {
      t,
      title,
      splitToggleLabel,
      splitToggleTooltip,
      ...chartSetup,
    };
  },
  View: ({
    t,
    title,
    splitToggleLabel,
    splitToggleTooltip,
    showSplit,
    onToggleSplit,
    hasData,
    chartContainerRef,
    tooltipRef,
    tooltipValues,
  }) => (
    <ChartContainer
      header={title}
      headerRight={
        <Tooltip title={splitToggleTooltip} placement="top">
          <div className="flex items-center gap-2">
            <span
              className={classNames('text-sm transition-colors', {
                'font-medium text-primary-medium': showSplit,
                'text-control-track': !showSplit,
              })}
            >
              {splitToggleLabel}
            </span>

            <Switch
              checked={showSplit}
              onChange={() => onToggleSplit()}
              inputProps={{
                'aria-label': splitToggleLabel,
              }}
              data-testid="dual-series-split-toggle"
            />
          </div>
        </Tooltip>
      }
      divider={false}
      fullHeight={true}
    >
      {hasData ? (
        <div
          ref={chartContainerRef}
          className="relative mt-3 min-h-[228px] w-full flex-1"
          data-testid="dual-series-area-chart"
        >
          {tooltipValues?.date && (
            <div
              ref={tooltipRef}
              className="absolute top-4 z-50 min-w-32 rounded-lg bg-white p-2 text-secondary-dark shadow-lg"
            >
              {tooltipValues.items.map(item => (
                <div
                  key={item.label}
                  className="flex items-center justify-between gap-3 text-sm"
                >
                  <span className="font-semibold" style={{ color: item.color }}>
                    {item.label}
                  </span>
                  <span className="money-display">{item.value}</span>
                </div>
              ))}

              <p className="mt-1 text-center text-xs">{tooltipValues.date}</p>
            </div>
          )}
        </div>
      ) : (
        <div
          className="flex min-h-[228px] flex-1 items-center justify-center font-semibold text-border-dark"
          data-testid="dual-series-area-chart-empty"
        >
          <p>{t('common.no-data', { ns: 'common' })}</p>
        </div>
      )}
    </ChartContainer>
  ),
});
